import { z } from 'zod';
import { cleanString } from './clean.js';
import { CompanyType, EngagementStatus } from './onboarding-enums.js';
import { VendorDocument } from './vendor.js';

// The primary internal directory list is keyed on the RESOURCE (person), with
// the vendor + latest engagement surfaced as columns (T-067). Each row drills
// into its vendor (`/vendors/:vendor_id`). Non-sensitive fields only.
export const ResourceListItem = z.object({
  id: z.string().uuid(),
  resource_name: z.string(),
  // The business the resource works under; the row links here for the drill-down.
  vendor_id: z.string().uuid(),
  business_name: z.string(),
  // Nullable since stub-backfilled vendors/resources carry only the core fields.
  company_type: CompanyType.nullable(),
  business_address_city: z.string().nullable(),
  business_address_state: z.string().nullable(),
  resource_email: z.string().nullable(),
  client_name: z.string().nullable(),
  engagement_status: EngagementStatus.nullable(),
  // Resource lifecycle (T-156): admin-set active flag + manual offboard date.
  // Distinct from engagement_status — a person can be inactive with an active
  // engagement (rolled off, contract not yet ended).
  active: z.boolean(),
  offboarded_date: z.string().nullable(),
  created_at: z.string(),
});
export type ResourceListItem = z.infer<typeof ResourceListItem>;

// A YYYY-MM-DD calendar date (the offboard date is a day, not a timestamp).
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const ResourceListResponse = z.object({
  items: z.array(ResourceListItem),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  page_size: z.number().int().positive(),
});
export type ResourceListResponse = z.infer<typeof ResourceListResponse>;

/**
 * Derived completeness (the "Incomplete" badge): a stub-backfilled record
 * carries only its core fields. "Complete" means the identity fields the
 * onboarding flow fills are present. No stored flag.
 */
export type ResourceCompletenessFields = Pick<
  ResourceListItem,
  'resource_email' | 'company_type' | 'business_address_city' | 'business_address_state'
>;

export function isResourceComplete(r: ResourceCompletenessFields): boolean {
  return (
    !!r.resource_email?.trim() &&
    !!r.company_type &&
    !!r.business_address_city?.trim() &&
    !!r.business_address_state?.trim()
  );
}

// ── Detail (T-090) ───────────────────────────────────────────────────
// The resource (person) page. Links out to its vendor + each engagement.
export const ResourceEngagementSummary = z.object({
  id: z.string().uuid(),
  client_name: z.string(),
  product_service: z.string(),
  status: EngagementStatus,
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
});
export type ResourceEngagementSummary = z.infer<typeof ResourceEngagementSummary>;

// ── Update (T-106) ───────────────────────────────────────────────────
// Editable subset of a resource's own (person) attributes, shared by the FE
// form resolver and the BE PATCH validator. Excludes worker_id (VMS-assigned
// during billing, not editable here) and vendor/engagement fields (their own
// records). Margin / referral fee are owner-gated (Wave 3) and not in scope.
export const ResourceUpdateInput = z
  .object({
    // Free-text fields are cleanString()-normalized (T-200), same as intake.
    resource_name: cleanString(z.string().min(1).max(200)).optional(),
    role: cleanString(z.string().max(200)).nullable().optional(),
    resource_email: z.string().email().max(320).nullable().optional(),
    mobile_phone: cleanString(z.string().max(50)).nullable().optional(),
    resource_mailing_address_street: cleanString(z.string().max(400)).nullable().optional(),
    resource_mailing_address_city: cleanString(z.string().max(200)).nullable().optional(),
    resource_mailing_address_state: cleanString(z.string().max(100)).nullable().optional(),
    resource_mailing_address_zip: cleanString(z.string().max(20)).nullable().optional(),
    // Lifecycle (T-156). Setting offboarded_date implies inactive (applied
    // server-side); active can also be toggled on its own without a date.
    active: z.boolean().optional(),
    offboarded_date: IsoDate.nullable().optional(),
  })
  .refine((obj) => Object.keys(obj).length > 0, {
    message: 'At least one field must be provided',
  });
export type ResourceUpdateInput = z.infer<typeof ResourceUpdateInput>;

export const ResourceDetail = z.object({
  id: z.string().uuid(),
  resource_name: z.string(),
  role: z.string().nullable(),
  worker_id: z.string().nullable(),
  resource_email: z.string().nullable(),
  // Generated uIT O365 mailbox (T-114). System-managed (not in ResourceUpdateInput):
  // set by Graph provisioning on acceptance, with a manual retry route.
  uit_email: z.string().nullable(),
  mobile_phone: z.string().nullable(),
  resource_mailing_address_street: z.string().nullable(),
  resource_mailing_address_city: z.string().nullable(),
  resource_mailing_address_state: z.string().nullable(),
  resource_mailing_address_zip: z.string().nullable(),
  // Vendor summary (the business the resource belongs to). company_type +
  // business_address_* also drive the "Incomplete" badge (isResourceComplete).
  vendor_id: z.string().uuid(),
  business_name: z.string(),
  company_type: CompanyType.nullable(),
  business_address_city: z.string().nullable(),
  business_address_state: z.string().nullable(),
  // Lifecycle (T-156).
  active: z.boolean(),
  offboarded_date: z.string().nullable(),
  engagements: z.array(ResourceEngagementSummary),
  documents: z.array(VendorDocument), // resource-scope documents
});
export type ResourceDetail = z.infer<typeof ResourceDetail>;

// ── uIT mailbox provisioning (T-114) ─────────────────────────────────
// Result of POST /api/resources/:id/uit-mailbox (the manual create/retry of the
// resource's O365 mailbox). The FE refetches the resource on a success status to
// pull the new uit_email; on a failure status it surfaces `message`.
export const UitMailboxResult = z.object({
  status: z.enum(['created', 'already_exists', 'not_configured', 'no_name', 'no_license', 'error']),
  email: z.string().nullable(),
  message: z.string().nullable(),
});
export type UitMailboxResult = z.infer<typeof UitMailboxResult>;

// Result of DELETE /api/resources/:id/uit-mailbox (manual mailbox removal, T-141).
export const UitMailboxRemoveResult = z.object({
  status: z.enum(['removed', 'none', 'not_configured', 'error']),
  message: z.string().nullable(),
});
export type UitMailboxRemoveResult = z.infer<typeof UitMailboxRemoveResult>;
